import React, { useState, useEffect, useCallback, useRef } from "react";
import { Rnd } from "react-rnd";
import styled from "styled-components";
import { CloseButton } from "styledComponents/buttons";
import { useResponsive } from "customHooks/useResponsive";
import { getYouTubeVideoId, isYouTubeUrl } from "../../utils/helper";
import { FLOATING_VIDEO_CONFIG } from "./floatingVideoConfig";

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const FloatingWindowWrapper = styled.div`
  box-sizing: border-box;
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 20px;
  gap: 12px;
  isolation: isolate;
  background: #ffffff;
  border: 1px solid #b4c6f9;
  box-shadow: 0px 4px 35px rgba(0, 14, 42, 0.15);
  border-radius: 12px;
  cursor: move;
`;

const HeaderTitle = styled.span`
  width: 280px;
  height: 28px;
  font-family: 'Mona Sans', sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 20px;
  line-height: 28px;
  color: #000E2A;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
  z-index: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Absolutely positioned top-right, per Figma Group 38778
const HeaderCloseButton = styled(CloseButton)`
  position: absolute;
  top: 4px;
  right: 4px;
  background: #d3e0fc;
  color: #353a46;
  z-index: 3;

  &:hover {
    background: #b7ccfa;
    color: #2266ef;
    transform: scale(1.15);
  }
`;

const MediaArea = styled.div`
  flex: 1;
  min-height: 0;
  order: 1;
  align-self: stretch;
  z-index: 1;
  width: 100%;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: auto;
  background: ${({ $bg }) =>
    $bg ? `url(${$bg}) center / cover no-repeat` : "#faf5f2"};
`;

const PlayButtonOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
`;

const PlayButtonWrapper = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 32px;
  background: linear-gradient(
    138.69deg,
    rgba(34, 102, 239, 0.65) 18.15%,
    rgba(108, 46, 242, 0.65) 78.01%
  );
  display: flex;
  align-items: center;
  justify-content: center;
`;

// CSS-only right-pointing triangle
const PlayIconTriangle = styled.div`
  width: 0;
  height: 0;
  border-top: 10px solid transparent;
  border-bottom: 10px solid transparent;
  border-left: 16px solid #ffffff;
  margin-left: 4px; /* optical centre adjustment */
`;

const VIEWPORT_GUTTER = 16;

const getViewportDimensions = () => ({
  width: typeof window !== "undefined" ? window.innerWidth : 1280,
  height: typeof window !== "undefined" ? window.innerHeight : 720,
});

const clampSizeToViewport = size => {
  const { width, height } = getViewportDimensions();

  return {
    width: Math.min(size.width, Math.max(300, width - VIEWPORT_GUTTER)),
    height: Math.min(size.height, Math.max(200, height - VIEWPORT_GUTTER)),
  };
};

// Convert ratio-based position into pixels using the remaining visible space
// after the floating window size is accounted for.
const ratioToPixels = ({ xRatio, yRatio }, size) => {
  const { width, height } = getViewportDimensions();
  const maxX = Math.max(0, width - size.width);
  const maxY = Math.max(0, height - size.height);

  return {
    x: Math.round(maxX * xRatio),
    y: Math.round(maxY * yRatio),
  };
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * FloatingVideoPlayer
 *
 * Renders a draggable, resizable floating video window whose content and
 * initial position/size are driven entirely by `FLOATING_VIDEO_CONFIG`.
 *
 * @param {string} pageKey — must match a key in FLOATING_VIDEO_CONFIG
 */
const FloatingVideoPlayer = ({ pageKey }) => {
  const config = FLOATING_VIDEO_CONFIG[pageKey];
  const { isMobile, isTablet } = useResponsive();

  // Returns the correct pixel position from config ratios for the current breakpoint.
  const getPositionForBreakpoint = useCallback(windowSize => {
    if (!config) return { x: 0, y: 0 };
    const ratios = isMobile
      ? config.mobilePosition ?? config.defaultPosition
      : isTablet
      ? config.tabletPosition ?? config.defaultPosition
      : config.defaultPosition;
    return ratioToPixels(ratios, windowSize);
  }, [config, isMobile, isTablet]);

  const [isOpen, setIsOpen] = useState(true);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  // Controlled position — initialised from config based on current breakpoint.
  const [position, setPosition] = useState(() => ({ x: 0, y: 0 }));

  // Controlled size — user can still resize freely via react-rnd handles.
  const [size, setSize] = useState(() => ({
    width: config?.defaultSize?.width ?? 300,
    height: config?.defaultSize?.height ?? 200,
  }));

  const applyResponsiveLayout = useCallback(currentSize => {
    const nextSize = clampSizeToViewport(currentSize);
    setSize(nextSize);
    setPosition(getPositionForBreakpoint(nextSize));
  }, [getPositionForBreakpoint]);

  const sizeRef = useRef(size);
  useEffect(() => {
    sizeRef.current = size;
  }, [size]);

  // Always keep a ref to the latest layout applier so the resize effect never goes stale.
  const applyResponsiveLayoutRef = useRef(applyResponsiveLayout);
  useEffect(() => {
    applyResponsiveLayoutRef.current = applyResponsiveLayout;
  }, [applyResponsiveLayout]);

  useEffect(() => {
    applyResponsiveLayout({
      width: config?.defaultSize?.width ?? 300,
      height: config?.defaultSize?.height ?? 200,
    });
  }, [applyResponsiveLayout, config]);

  useEffect(() => {
    applyResponsiveLayout(size);
  }, [isMobile, isTablet]);

  const resizeTimerRef = useRef(null);
  useEffect(() => {
    const onResize = () => {
      clearTimeout(resizeTimerRef.current);
      resizeTimerRef.current = setTimeout(() => {
        applyResponsiveLayoutRef.current(sizeRef.current);
      }, 100);
    };

    window.addEventListener("resize", onResize);
    onResize();

    return () => {
      window.removeEventListener("resize", onResize);
      clearTimeout(resizeTimerRef.current);
    };
  }, []);

  // Unknown pageKey or window closed — render nothing
  if (!config || !isOpen) return null;

  const { title, videoUrl, thumbnailImage } = config;

  return (
    <Rnd
      position={position}
      size={size}
      onDragStop={(e, d) => setPosition({ x: d.x, y: d.y })}
      onResizeStop={(e, _dir, ref, _delta, newPosition) => {
        setSize(clampSizeToViewport({
          width: ref.offsetWidth,
          height: ref.offsetHeight,
        }));
        setPosition(newPosition);
      }}
      minWidth={300}
      minHeight={200}
      bounds="window"
      cancel=".fvp-no-drag"
      style={{ zIndex: 1200 }}
    >
      <FloatingWindowWrapper>
        {/* ── Title ── */}
        <HeaderTitle>{title}</HeaderTitle>

        {/* ── Close button (absolutely positioned top-right) ── */}
        <HeaderCloseButton
          className="fvp-no-drag"
          onClick={() => setIsOpen(false)}
          aria-label="Close video player"
        >
          ×
        </HeaderCloseButton>

        {/* ── Media area ── */}
        <MediaArea
          className="fvp-no-drag"
          $bg={!isVideoPlaying && thumbnailImage ? thumbnailImage : undefined}
        >
          {!isVideoPlaying ? (
            <>
              <PlayButtonOverlay
                onClick={() => setIsVideoPlaying(true)}
                role="button"
                aria-label="Play video"
              >
                <PlayButtonWrapper>
                  <PlayIconTriangle />
                </PlayButtonWrapper>
              </PlayButtonOverlay>
            </>
          ) : isYouTubeUrl(videoUrl) ? (
            <iframe
              title={title}
              width="100%"
              height="100%"
              src={`https://www.youtube.com/embed/${getYouTubeVideoId(videoUrl)}?autoplay=1`}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ display: "block", border: "none" }}
            />
          ) : (
            <video
              src={videoUrl}
              autoPlay
              controls
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
              onEnded={() => setIsVideoPlaying(false)}
            />
          )}
        </MediaArea>
      </FloatingWindowWrapper>
    </Rnd>
  );
};

export default FloatingVideoPlayer;
